/**
 * Gallery Layout Strategy
 *
 * Gallery mode displays one dimension (run, step, or index) as rows,
 * with other dimensions controlled via sliders in the header/footer.
 *
 * This file delegates to axis-specific strategies for implementation.
 */

import {type MediaBrowserPanelConfig} from '../common';
import {
  getAggregatedAvailableIndices,
  getAggregatedAvailableSteps,
} from '../utils/stepUtils';
import {getGalleryAxisStrategy} from './gallery';
import {type LayoutStrategy} from './LayoutStrategy';
import {
  type ControlData,
  type GridData,
  type RowData,
  type SlotRegistry,
  type TransformInput,
} from './types';

export const GalleryLayout: LayoutStrategy = {
  id: 'gallery',

  transform(input: TransformInput): GridData {
    const {config, mediaType} = input;
    const {gallerySettings, mediaKeys} = config;
    const axis = gallerySettings?.axis ?? 'run';
    const contentRowFillsPanel = mediaType === 'video';

    if (!mediaKeys || mediaKeys.length === 0) {
      return {
        rows: [],
        metadata: {
          layoutType: 'gallery',
          totalCells: 0,
          contentRowFillsPanel,
        },
      };
    }

    const strategy = getGalleryAxisStrategy(axis);
    // `null` means "explicitly show all" — same as no limit here.
    const axisValues = strategy.getAxisValues(
      input,
      config.maxGalleryItems ?? undefined
    );
    const rows = strategy.createRows(input, axisValues);

    // Flatten all cells and chunk into visual rows (each row has up to columnCount cells)
    // This produces a standard structure: each row is a visual row with cells ordered left-to-right
    const allCells = rows.flatMap(row => row.cells);
    const columnCount = config.columnCount ?? 3;
    const visualRows: typeof rows = [];
    for (let i = 0; i < allCells.length; i += columnCount) {
      visualRows.push({
        cells: allCells.slice(i, i + columnCount),
        rowKey: `gallery-row-${Math.floor(i / columnCount)}`,
      });
    }
    const totalCells = allCells.length;

    return {
      rows: visualRows,
      metadata: {
        layoutType: 'gallery',
        totalCells,
        cellStartIndex: config.page?.start ?? 0,
        contentRowFillsPanel,
        axisData: {
          x: [0], // Single column in gallery
          // Filter out null values from axis values to match old behavior
          y: axisValues.filter((v): v is number => v != null),
        },
      },
    };
  },

  buildSlotRegistry(
    config: MediaBrowserPanelConfig,
    stepMeta?: TransformInput['stepMeta'],
    _isFullScreen: boolean = false,
    isSingleRunView?: boolean
  ): SlotRegistry {
    const axis = config.gallerySettings?.axis ?? 'run';

    const strategy = getGalleryAxisStrategy(axis);
    return strategy.buildSlotRegistry(stepMeta, isSingleRunView);
  },

  buildRowData(_input: TransformInput, gridData: GridData): RowData[] {
    // Gallery rows don't carry per-row dimension overrides — values are
    // embedded in each cell's tileSpec. Return empty entries.
    return Array.from({length: gridData.rows.length}, () => ({}));
  },

  buildControlData(input: TransformInput, _gridData: GridData): ControlData[] {
    const {config, stepMeta, runs, currentStep, currentIndex} = input;
    const columnCount = config.columnCount ?? 3;
    const mediaKeys = config.mediaKeys ?? [];
    const numMediaKeys = mediaKeys.length;

    const run = runs[0]?.name;
    const step = config.stepIndex ?? currentStep;
    const index = config.mediaIndex ?? currentIndex;

    const availableSteps = getAggregatedAvailableSteps(stepMeta);
    const availableIndices = getAggregatedAvailableIndices(stepMeta);

    return Array.from({length: columnCount}, (_, i) => {
      const keyIndex = numMediaKeys > 1 ? Math.min(i, numMediaKeys - 1) : 0;
      const mediaKey = mediaKeys[keyIndex];
      return {step, index, run, mediaKey, availableSteps, availableIndices};
    });
  },
};
